import axios from 'axios';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';

const baseURL = "https://api.trubetx.com";

interface WcApiConfig {
  id: number;
  ag_code: string;
  ag_token: string;
  ag_secret: string;
  api_endpoint: string;
  currency: string;
  language: string;
  created_at?: string;
}

const WcConfig = () => {
  const [configs, setConfigs] = useState<WcApiConfig[]>([]);
  const [newConfig, setNewConfig] = useState<Omit<WcApiConfig, "id" | "created_at">>({
    ag_code: "",
    ag_token: "",
    ag_secret: "",
    api_endpoint: "",
    currency: "",
    language: "",
  });

  const [editConfig, setEditConfig] = useState<WcApiConfig | null>(null);
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("email");
    navigate("/login");
  };

  // Fetch WC API Config
  const fetchConfigs = async () => {
    try {
      const response = await axios.get(`${baseURL}/admin/getWcApiConfig`);
      if (response.data.status) {
        setConfigs(response.data.data);
      } else {
        toast(response.data.message || "Failed to fetch config.");
      }
    } catch (error) {
      console.error("Error fetching WC config:", error);
      toast("Error fetching WC config.");
    }
  };

  useEffect(() => {
    fetchConfigs();
  }, []);

  // Handle new config input
  const handleNewChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setNewConfig((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle edit change
  const handleEditChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    if (editConfig) {
      const { name, value } = e.target;
      setEditConfig((prev) => ({
        ...prev!,
        [name]: value,
      }));
    }
  };

  // Add WC config
  const handleAddConfig = async (e: React.FormEvent) => {
    e.preventDefault();

    try {

      if(!newConfig.ag_code || !newConfig.ag_token || !newConfig.ag_secret || !newConfig.api_endpoint || !newConfig.currency || !newConfig.language){
        toast("Please fill in all fields.");
        return;
      }

      const response = await axios.post(
        `${baseURL}/admin/createWcApiConfig`,
        newConfig
      );

      if (response.data.status) {
        toast("WC API Config added successfully!");
        setNewConfig({
          ag_code: "",
          ag_token: "",
          ag_secret: "",
          api_endpoint: "",
          currency: "",
          language: "",
        });
        fetchConfigs();
      } else {
        toast(response.data.message);
      }
    } catch (error: any) {
      console.error("Error adding WC config:", error);
      toast(error.response?.data?.message || "Error adding WC config.");
    }
  };

  // Update config
  const handleUpdateConfig = async (e: React.FormEvent) => {
    e.preventDefault();

    if (editConfig) {
      try {
        const response = await axios.post(
          `${baseURL}/admin/updateWcApiConfig/${editConfig.id}`,
          editConfig
        );

        if (response.data.status) {
          toast("WC API Config updated successfully!");
          setEditConfig(null);
          fetchConfigs();
        } else {
          toast(response.data.message);
        }
      } catch (error) {
        console.error("Error updating config:", error);
        toast("Error updating WC config.");
      }
    }
  };

  // Delete config
  const handleDeleteConfig = async (id: number) => {
    try {
      const response = await axios.delete(
        `${baseURL}/admin/deleteWcApiConfig/${id}`
      );

      if (response.data.status) {
        toast("WC API Config deleted successfully!");
        fetchConfigs();
      } else {
        toast(response.data.message || "Failed to delete config.");
      }
    } catch (error) {
      console.error("Error deleting WC config:", error);
      toast("Error deleting WC config.");
    }
  };

   const masked = (val: string) => "*".repeat(8);


  return (
    <div className="ml-0 p-4 bg-white">
      <h2 className="text-2xl font-semibold mb-4">WC API Config Management</h2>
      <hr className="p-5" />

      {/* Add New Config */}
      <div className="mb-8">
        <h3 className="text-xl mb-4 font-semibold">Add New Config</h3>
        <form onSubmit={handleAddConfig} className="space-y-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          {Object.keys(newConfig).map((key) => (
            <div key={key}>
              <label className="capitalize">{key.replace("_", " ")}</label>
              <input
                type="text"
                name={key}
                value={(newConfig as any)[key]}
                onChange={handleNewChange}
                className="border rounded p-2 w-full mt-1"
              />
            </div>
          ))}

          <button
            type="submit"
            className="bg-blue-500 text-white py-2 px-4 rounded"
          >
            Add Config
          </button>
        </form>
      </div>

      {/* Edit Config */}
      {editConfig && (
        <div className="mb-8">
          <h3 className="text-xl">Edit WC API Config</h3>
          <form onSubmit={handleUpdateConfig} className="space-y-4">
            <div>
              <label>ID</label>
              <input
                type="text"
                value={editConfig.id}
                disabled
                className="border rounded p-2 w-full bg-gray-300"
              />
            </div>

            {Object.keys(newConfig).map((key) => (
              <div key={key}>
                <label className="capitalize">{key.replace("_", " ")}</label>
                <input
                  type="text"
                  name={key}
                  value={(editConfig as any)[key]}
                  onChange={handleEditChange}
                  className="border rounded p-2 w-full"
                />
              </div>
            ))}

            <button
              type="submit"
              className="bg-green-500 text-white py-2 px-4 rounded"
            >
              Update Config
            </button>
            <button
              type="button"
              onClick={() => setEditConfig(null)}
              className="bg-red-500 text-white py-2 px-4 rounded ml-2"
            >
              Cancel
            </button>
          </form>
        </div>
      )}

      {/* Display Configs */}
      <div>
        {configs.length > 0 ? (
          <table className="table-auto w-full text-center">
            <thead>
              <tr>
                <th className="border px-4 py-2">ID</th>
                <th className="border px-4 py-2">AG Code</th>
                <th className="border px-4 py-2">Token</th>
                <th className="border px-4 py-2">Secret</th>
                <th className="border px-4 py-2">Endpoint</th>
                <th className="border px-4 py-2">Currency</th>
                <th className="border px-4 py-2">Language</th>
                <th className="border px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {configs.map((cfg) => (
                <tr key={cfg.id}>
                  <td className="border px-4 py-2">{cfg.id}</td>
                  <td className="border px-4 py-2">{cfg.ag_code}</td>
                   <td className="border px-4 py-2">
                    {masked(cfg.ag_token)}
                  </td>

                  <td className="border px-4 py-2">
                    {masked(cfg.ag_secret)}
                  </td>
                  <td className="border px-4 py-2">{cfg.api_endpoint}</td>
                  <td className="border px-4 py-2">{cfg.currency}</td>
                  <td className="border px-4 py-2">{cfg.language}</td>
                  <td className="border px-4 py-2">
                    <button
                      onClick={() => setEditConfig(cfg)}
                      className="bg-yellow-500 text-white py-1 px-3 rounded"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteConfig(cfg.id)}
                      className="bg-red-500 text-white py-1 px-3 rounded ml-2"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p>No WC API configurations available.</p>
        )}
      </div>

      <ToastContainer />
    </div>
  );
};

export default WcConfig;
